const TRUNCATION_TOLERANCE_PX = 1; // Allow 1px tolerance to account for minor rounding/layout differences.

/**
 * @param element {HTMLElement}
 * @param position {'left' | 'right' | 'top' | 'bottom'}
 * @param fallbackPosition {'left' | 'right' | 'top' | 'bottom'}
 */
export const setTitleIfTextOverflows = (element, position, fallbackPosition) => {
	if (!element) {
		return;
	}

	const isTruncated = isElementTruncated(element);

	if (!isTruncated || !element.textContent) {
		removeTitle(element);
	} else {
		if (getTooltipBuilder()) {
			buildImtTooltip(element, position, fallbackPosition);
		} else {
			element.setAttribute('title', element.textContent);
		}
	}
};

/**
 * Removes the title attribute from the given element.
 * @param element {HTMLElement}
 * @returns {void}
 */
export const removeTitle = (element) => {
	if (element?._tooltipBuilder) {
		element._tooltipBuilder.removeTooltip();
		element._tooltipBuilder.withTitle(element.innerText);
	}

	element?.removeAttribute('title');
};

/**
 * @param element {HTMLElement}
 * @param position {'left' | 'right' | 'top' | 'bottom'}
 * @param fallbackPosition {'left' | 'right' | 'top' | 'bottom'}
 */
const buildImtTooltip = (element, position, fallbackPosition) => {
	if (element?._tooltipBuilder) {
		// If tooltip already exists, just update the text, internal listeners will handle the rest
		element._tooltipBuilder.withTitle(element.innerText);
		return;
	}

	const tooltipBuilder = getTooltipBuilder()(element.innerText)
		.withPosition(position)
		.withFallbackPositions([fallbackPosition]);

	tooltipBuilder.toElement(element).then(() => {
		// Trigger mouseenter to show the tooltip immediately
		element.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
	});

	element._tooltipBuilder = tooltipBuilder;
};

/**
 * @param node {Node}
 * @returns {boolean}
 */
const isElementTruncated = (node) => {
	// Text nodes do not have scrollWidth/clientWidth, so we need to measure their bounding rect
	if (node.nodeType === Node.TEXT_NODE) {
		return isTextNodeTruncated(node);
	}

	if (node.nodeType === Node.ELEMENT_NODE) {
		return isElementNodeTruncated(node);
	}

	return false;
};

/**
 * @param element {Text}
 * @returns {boolean}
 */
const isTextNodeTruncated = (element) => {
	// We cannot measure text nodes directly, so we create a range to get its bounding rect
	const range = document.createRange();
	range.selectNodeContents(element);

	if (!range.getBoundingClientRect) {
		return false;
	}

	const rect = range.getBoundingClientRect();

	const parent = element.parentElement;

	if (!parent) return false;

	const parentRect = parent.getBoundingClientRect();
	const parentStyle = window.getComputedStyle(parent);

	// We need to subtract padding and border from the parent's bounding rect right edge
	const parentBorderRight = parseFloat(parentStyle.borderRightWidth) || 0;
	const parentPaddingRight = parseFloat(parentStyle.paddingRight) || 0;
	const parentContentRight = parentRect.right - parentPaddingRight - parentBorderRight;

	// Check if text extends past the parent's right content edge
	return rect.right > parentContentRight + TRUNCATION_TOLERANCE_PX;
};

/**
 * @param element {HTMLElement}
 * @returns {boolean}
 * */
const isElementNodeTruncated = (element) => {
	const isTruncated = element.scrollWidth > element.clientWidth + TRUNCATION_TOLERANCE_PX;

	if (isTruncated) {
		return true;
	}

	for (const child of element.childNodes) {
		if (isElementTruncated(child)) {
			return true;
		}
	}

	return false;
};

/**
 * @description JSDoc typedef for TooltipBuilder implemented in Inspector.
 * @typedef {Object} TooltipBuilder
 * @property {(title: string) => TooltipBuilder} withTitle Sets the title text.
 * @property {(placement: 'left' | 'right' | 'top' | 'bottom') => TooltipBuilder} withPosition Sets the preferred position (top, bottom, etc).
 * @property {(delay: number) => TooltipBuilder} withInDelay Sets the show delay in ms.
 * @property {(delay: number) => TooltipBuilder} withOutDelay Sets the hide delay in ms.
 * @property {(offset: number) => TooltipBuilder} withOffset Sets the offset distance in pixels.
 * @property {(disabled: boolean) => TooltipBuilder} withDisabled Enables/disables the tooltip.
 * @property {(positions: Array<'left' | 'right' | 'top' | 'bottom'>) => TooltipBuilder} withFallbackPositions Sets fallback positions if the preferred one doesn't fit.
 * @property {(el: HTMLElement | { __dom: HTMLElement }) => Promise<void>} toElement Attaches the tooltip to a DOM element.
 * @property {() => void} removeTooltip Hides and removes the tooltip from the DOM.
 * @returns {(text: string) => TooltipBuilder}
 */
const getTooltipBuilder = () => {
	const ImtTooltipClass = customElements.get('imt-tooltip');
	if (!ImtTooltipClass) {
		return null;
	}

	return ImtTooltipClass.builder;
};
